import React from "react";
import { Link } from "react-router-dom";
import { api, unwrap } from "@/lib/api";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { ClipboardCheck, Users, Megaphone, ChevronRight, CalendarClock } from "lucide-react";
import { formatDate } from "@/lib/utils";
import { useAuth } from "@/lib/auth";
import { useI18n } from "@/i18n";
import { levelDisplayName } from "@/lib/levelLabels";

const scheduleCopy = {
  en: {
    title: "Weekly schedule",
    subtitle: "Your assigned lessons this week",
    noLessons: "No lessons are assigned this week.",
    loadError: "Unable to load your schedule.",
    retry: "Retry",
    unassigned: "Unassigned section",
  },
  ar: {
    title: "الجدول الأسبوعي",
    subtitle: "حصصك المسندة لهذا الأسبوع",
    noLessons: "لا توجد حصص مسندة هذا الأسبوع.",
    loadError: "تعذر تحميل جدولك الدراسي.",
    retry: "إعادة المحاولة",
    unassigned: "شعبة غير مسندة",
  },
};

const scheduleDays = {
  en: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
  ar: ["الاثنين", "الثلاثاء", "الأربعاء", "الخميس", "الجمعة", "السبت", "الأحد"],
};

export default function TeacherHome() {
  const { user } = useAuth();
  const { language, t } = useI18n();
  const copy = scheduleCopy[language] || scheduleCopy.en;
  const days = scheduleDays[language] || scheduleDays.en;
  const { data, isLoading } = useQuery({
    queryKey: ["teacher-home"],
    queryFn: async () => unwrap(await api.get("/teacher/home")),
  });
  const teacherUserId = user?.user?.id;
  const {
    data: schedule,
    isLoading: isScheduleLoading,
    isError: isScheduleError,
    refetch: refetchSchedule,
  } = useQuery({
    queryKey: ["teacher-timetable", teacherUserId],
    queryFn: async () => unwrap(await api.get(`/timetable/teacher/${teacherUserId}`)),
    enabled: Boolean(teacherUserId),
  });

  if (isLoading) return <Skeleton className="h-96 w-full" />;
  const sections = data?.sections || [];
  const circulars = data?.circulars || [];

  return (
    <div className="space-y-6" data-testid="teacher-home">
      <div>
        <div className="text-xs uppercase tracking-widest text-[var(--text-muted)] font-semibold">
          {formatDate(data?.today)}
        </div>
        <h1 className="font-display text-2xl font-bold text-[var(--text-primary)] mt-1">
          {t("teacher.greeting", { name: user?.user?.full_name?.split(" ")[0] })}
        </h1>
      </div>

      <div>
        <div className="text-xs uppercase tracking-widest text-[var(--text-muted)] font-semibold mb-3">
          {t("teacher.yourSections")}
        </div>
        <div className="space-y-3 stagger">
          {sections.map((s) => (
            <Link key={s.id} to={`/teacher/attendance?section_id=${s.id}`} data-testid={`section-${s.name}`}>
              <Card className="border-[var(--subtle)] lift-card">
                <CardContent className="p-5 flex items-center gap-4">
                  <div className="h-12 w-12 rounded-md bg-[var(--brand)] text-white flex items-center justify-center font-display font-bold flex-shrink-0">
                    {levelDisplayName(s.grade, language)?.replace("Class ", "")}
                    <span className="text-sm opacity-70">{s.name}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-[var(--text-primary)]">
                      {t("teacher.gradeSection", { grade: levelDisplayName(s.grade, language), name: s.name })}
                    </div>
                    <div className="text-xs text-[var(--text-muted)] mt-1 flex items-center gap-3">
                      <span className="inline-flex items-center gap-1">
                        <Users className="h-3 w-3" /> {t("teacher.studentsCount", { count: s.enrolled })}
                      </span>
                      {s.pending_attendance && (
                        <span className="inline-flex items-center gap-1 text-[var(--accent)] font-semibold">
                          <ClipboardCheck className="h-3 w-3" /> {t("teacher.attendancePending")}
                        </span>
                      )}
                    </div>
                  </div>
                  <ChevronRight className="h-5 w-5 text-[var(--text-muted)] rtl:rotate-180" />
                </CardContent>
              </Card>
            </Link>
          ))}
          {sections.length === 0 && (
            <Card className="border-[var(--subtle)]"><CardContent className="py-10 text-center text-[var(--text-secondary)]">{t("teacher.noSectionsAssigned")}</CardContent></Card>
          )}
        </div>
      </div>

      <section data-testid="teacher-timetable" className="space-y-3">
        <div className="flex items-start gap-3">
          <CalendarClock className="h-5 w-5 mt-0.5 text-[var(--accent)]" />
          <div>
            <h2 className="font-display text-xl font-bold text-[var(--text-primary)]">{copy.title}</h2>
            <p className="text-sm text-[var(--text-muted)] mt-0.5">{copy.subtitle}</p>
          </div>
        </div>

        {isScheduleLoading ? <Skeleton className="h-52 w-full" /> : isScheduleError ? (
          <Card className="border-[var(--subtle)]">
            <CardContent className="p-5 flex flex-col items-start gap-3">
              <p className="text-sm text-[var(--text-secondary)]">{copy.loadError}</p>
              <Button variant="outline" onClick={() => refetchSchedule()} data-testid="retry-teacher-timetable">{copy.retry}</Button>
            </CardContent>
          </Card>
        ) : (schedule || []).length === 0 ? (
          <Card className="border-[var(--subtle)]">
            <CardContent className="p-5 text-sm text-[var(--text-secondary)]">{copy.noLessons}</CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {days.map((day, dayIndex) => {
              const daySlots = (schedule || []).filter((slot) => slot.day_of_week === dayIndex);
              if (!daySlots.length) return null;
              return (
                <Card key={day} className="border-[var(--subtle)]" data-testid={`teacher-schedule-day-${dayIndex}`}>
                  <CardContent className="p-4">
                    <h3 className="text-sm font-semibold text-[var(--text-primary)]">{day}</h3>
                    <div className="mt-3 space-y-2">
                      {daySlots.map((slot) => (
                        <div key={slot.id} className="flex items-center gap-3 border-s-2 border-[var(--accent)] bg-[var(--bg)] px-3 py-2">
                          <div className="font-mono-num text-xs text-[var(--text-secondary)] shrink-0">{slot.start_time} - {slot.end_time}</div>
                          <div className="min-w-0">
                            <div className="text-sm font-semibold text-[var(--text-primary)] truncate">{slot.subject?.name}</div>
                            <div className="text-xs text-[var(--text-muted)] truncate">{levelDisplayName(slot.section?.grade, language)} {slot.section?.name || copy.unassigned}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </section>

      {circulars.length > 0 && (
        <div>
          <div className="text-xs uppercase tracking-widest text-[var(--text-muted)] font-semibold mb-3">{t("teacher.latestCirculars")}</div>
          <div className="space-y-2">
            {circulars.map((c) => (
              <Card key={c.id} className="border-[var(--subtle)]">
                <CardContent className="p-4 flex items-center gap-3">
                  <Megaphone className="h-4 w-4 text-[var(--accent)]" />
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-sm">{c.title}</div>
                    <div className="text-xs text-[var(--text-muted)]">{formatDate(c.publish_at)}</div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
